import { useVerify } from '@entities/Authentication/model.ts';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CodeModal } from '@pages/personal/client/editor-page/Modals/CodeModal/index.tsx';
import { getTitle } from '@pages/personal/client/editor-page/Modals/helpers.ts';
import { useEditorClientSharedState } from '@pages/personal/client/editor-page/model.ts';
import { useSupportPageRedirect } from '@pages/support/model.ts';
import { doFormatEmail } from '@shared/helpers/index.ts';
import { useUser } from '@shared/hooks/index.ts';
import { useOpenEmailVerify } from './model.ts';

export const VerificationEmailModal = () => {
  const navigate = useNavigate();
  const { state, closeEmailVerificationModal } = useOpenEmailVerify();
  const { updateEditorClientSharedState } = useEditorClientSharedState();
  const { onEmailVerification, onCheckEmailVerification } = useVerify();
  const { user } = useUser();
  const { redirectToSupportPage } = useSupportPageRedirect();
  const [passCode, setPassCode] = useState('');
  const [showTimer, setShowTimer] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [step, setStep] = useState(1);

  const onCloseModal = useCallback(
    ({ passCode, isSuccess, value }: { isSuccess: boolean; value?: string; passCode?: string }) => {
      setErrorMessage('');
      setStep(1);
      setPassCode('');
      closeEmailVerificationModal({ value, passCode, isSuccess });
    },
    [setErrorMessage, setStep, closeEmailVerificationModal]
  );

  const onRepeatButtonClick = useCallback(async () => {
    if (step === 1) {
      await onEmailVerification({
        verification_type: state.verification_type,
        email: state?.value || user?.email,
        onError: (errMessage) => setErrorMessage(errMessage),
        onSuccess: () => {
          setShowTimer(true);
        },
      });
      setStep(2);
    } else {
      redirectToSupportPage();
      onCloseModal({ isSuccess: false });
    }
  }, [
    user,
    navigate,
    step,
    setStep,
    onEmailVerification,
    state,
    setShowTimer,
    setErrorMessage,
    onCloseModal,
  ]);
  const onReadyButtonClick = useCallback(async () => {
    await onCheckEmailVerification({
      verification_type: 'registration',
      email: state?.value || user?.email,
      passcode: passCode,
      onError: (errMessage) => setErrorMessage(errMessage),
      onSuccess: () => {
        updateEditorClientSharedState({ email: state?.value, email_code: passCode });
        onCloseModal({ isSuccess: true, value: state?.value, passCode });
      },
    });
  }, [
    user,
    state,
    passCode,
    onCheckEmailVerification,
    updateEditorClientSharedState,
    onCloseModal,
  ]);

  const repeatButtonText = step === 2 ? 'Техническая поддержка' : 'Отправить повторно';

  return (
    <CodeModal
      passCode={passCode}
      setPassCode={setPassCode}
      text={`Введите код подтверждения из письма, отправленного на адрес ${doFormatEmail(state?.value || user?.email)}`}
      onReadyButtonClick={onReadyButtonClick}
      title={getTitle(state?.verification_type)}
      showTimer={showTimer}
      additionalText={'Если Вы не получили письмо, проверьте спам или нажмите “Отправить повторно”'}
      cleanErrorMessage={() => setErrorMessage('')}
      errorMessage={errorMessage}
      onRepeatButtonClick={onRepeatButtonClick}
      repeatButtonText={repeatButtonText}
      onModalClose={() => onCloseModal({ isSuccess: false })}
      setShowTimerFalse={() => setShowTimer(false)}
      isOpen={state?.isModalOpen}
    />
  );
};
